import { useState, useEffect, useCallback } from 'react';

interface UseVoiceInputProps {
  onTranscript: (transcript: string) => void;
  disabled?: boolean;
}

interface UseVoiceInputReturn {
  isSupported: boolean;
  isRecording: boolean;
  startRecording: (e: React.MouseEvent) => void;
}

export const useVoiceInput = ({
  onTranscript,
  disabled = false
}: UseVoiceInputProps): UseVoiceInputReturn => {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);

  useEffect(() => {
    const recognitionAvailable =
      'SpeechRecognition' in window ||
      ('webkitSpeechRecognition' in window &&
        (window as any).webkitSpeechRecognition);

    if (recognitionAvailable) {
      setIsSupported(true);
    } else {
      setIsSupported(false);
      // Silently ignore unsupported browser features
    }
  }, []);

  const startRecording = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (disabled || !isSupported) return;

      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        onTranscript(transcript);
      };

      recognition.onend = () => {
        recognition.stop();
        setIsRecording(false);
      };

      try {
        recognition.start();
        setIsRecording(true);
      } catch (error) {
        // Silently ignore speech recognition errors
        setIsRecording(false);
      }
    },
    [disabled, isSupported, onTranscript]
  );

  return {
    isSupported,
    isRecording,
    startRecording
  };
};
